import { useState, useEffect } from 'react';
import { SlideModal } from '@podus/ui';

type View = 'confirm' | 'success';

interface ConfirmActionModalProps {
    open: boolean;
    onClose: () => void;
    /** Confirmation step copy */
    title: string;
    description: string;
    confirmLabel: string;
    /** Success step copy (shown after onConfirm resolves) */
    successTitle: string;
    successDescription: string;
    /** The actual API call. Modal manages loading + success state. */
    onConfirm: () => Promise<void>;
    /** Confirm button color. Use 'danger' for destructive actions (block, delete). */
    variant?: 'danger' | 'primary';
}

/**
 * Generic two-step modal:
 *   1. Asks the user to confirm an action.
 *   2. On success, swaps to a green-check confirmation panel with a Close button.
 *
 * Resets back to the confirm view whenever it's reopened.
 */
export default function ConfirmActionModal({
    open,
    onClose,
    title,
    description,
    confirmLabel,
    successTitle,
    successDescription,
    onConfirm,
    variant = 'primary',
}: ConfirmActionModalProps) {
    const [view, setView] = useState<View>('confirm');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (open) {
            setView('confirm');
            setIsLoading(false);
            setError('');
        }
    }, [open]);

    const handleConfirm = async () => {
        setIsLoading(true);
        setError('');
        try {
            await onConfirm();
            setView('success');
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Action failed. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const confirmBtnColor =
        variant === 'danger'
            ? 'bg-red-500 hover:bg-red-600'
            : 'bg-secondary hover:bg-secondary-400';

    return (
        <SlideModal open={open} onClose={onClose}>
            {view === 'confirm' ? (
                <div className="flex flex-1 flex-col px-8 pb-6 text-center">
                    <div className="flex flex-1 flex-col items-center justify-center">
                        {/* Question icon */}
                        <div className="flex h-20 w-20 items-center justify-center rounded-full bg-[#FEF3C7]">
                            <svg width="40" height="40" viewBox="0 0 24 24" fill="none">
                                <path
                                    d="M12 9v4m0 4h.01M10.29 3.86 1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                                    stroke="#F59E0B"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </div>

                        <h2 className="mt-6 text-xl font-bold text-secondary">{title}</h2>

                        <p className="mt-4 max-w-xs text-[15px] leading-relaxed text-[#888]">{description}</p>

                        {error && <p className="mt-4 text-sm text-red-500">{error}</p>}
                    </div>

                    <div className="flex gap-3">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={isLoading}
                            className="flex flex-1 items-center justify-center rounded-xl border border-[#E0E0E0] bg-white py-4 text-[15px] font-bold text-secondary transition-all hover:bg-[#f5f5f5] disabled:opacity-60"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleConfirm}
                            disabled={isLoading}
                            className={`flex flex-1 items-center justify-center rounded-xl py-4 text-[15px] font-bold text-white transition-all disabled:opacity-60 ${confirmBtnColor}`}
                        >
                            {isLoading ? 'Please wait…' : confirmLabel}
                        </button>
                    </div>
                </div>
            ) : (
                /* ── Success View ── */
                <div className="flex flex-1 flex-col px-8 pb-6 text-center">
                    <div className="flex flex-1 flex-col items-center justify-center">
                        <svg width="80" height="80" viewBox="0 0 80 80" fill="none">
                            <circle cx="40" cy="40" r="36" fill="#22c55e" />
                            <circle cx="40" cy="40" r="36" fill="url(#confirm-badge-grad)" />
                            {[0, 45, 90, 135, 180, 225, 270, 315].map((angle) => (
                                <circle
                                    key={angle}
                                    cx={40 + 30 * Math.cos((angle * Math.PI) / 180)}
                                    cy={40 + 30 * Math.sin((angle * Math.PI) / 180)}
                                    r="10"
                                    fill="#22c55e"
                                />
                            ))}
                            <circle cx="40" cy="40" r="24" fill="#22c55e" />
                            <path
                                d="M28 40l8 8 16-16"
                                stroke="white"
                                strokeWidth="4"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                fill="none"
                            />
                            <defs>
                                <radialGradient id="confirm-badge-grad" cx="0.5" cy="0.3" r="0.7">
                                    <stop offset="0%" stopColor="#4ade80" />
                                    <stop offset="100%" stopColor="#16a34a" />
                                </radialGradient>
                            </defs>
                        </svg>

                        <h2 className="mt-6 text-xl font-bold text-secondary">{successTitle}</h2>

                        <p className="mt-4 max-w-xs text-[15px] leading-relaxed text-[#888]">{successDescription}</p>
                    </div>

                    <button
                        type="button"
                        onClick={onClose}
                        className="flex w-full items-center justify-center rounded-xl bg-secondary py-4 text-[15px] font-bold text-white transition-all hover:bg-secondary-400"
                    >
                        Close
                    </button>
                </div>
            )}
        </SlideModal>
    );
}
